import React, { useState, useMemo, useCallback } from 'react';
import { Search, ChevronDown, X, HelpCircle } from 'lucide-react';
import HeaderSSR from '../../components/HeaderSSR';
import FooterSSR from '../../components/FooterSSR';
import { FAQ_META, FAQ_CATEGORIES, type FAQItem, type FAQCategory } from '../../../data/faqContent';

// ============================================================
// ACCORDION ITEM COMPONENT
// ============================================================

interface AccordionItemProps {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}

function AccordionItem({ item, isOpen, onToggle }: AccordionItemProps) {
  return (
    <div className="border-b border-brand-border last:border-b-0">
      <button
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="w-full flex items-start justify-between gap-4 py-5 px-1 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 rounded-lg transition-colors hover:bg-gray-50/50"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${item.id}`}
      >
        <h3 className="text-base md:text-lg font-semibold text-gray-900 pr-4 group-hover:text-brand-primary transition-colors">
          {item.question}
        </h3>
        <span
          className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary transition-transform duration-200 ease-out ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          <ChevronDown size={16} />
        </span>
      </button>
      <div
        id={`faq-answer-${item.id}`}
        className={`overflow-hidden transition-all duration-200 ease-out ${
          isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
        role="region"
        aria-labelledby={`faq-question-${item.id}`}
      >
        <div className="pb-5 px-1">
          <div
            className="prose prose-gray max-w-none text-gray-600 leading-relaxed
              [&_p]:mb-3 [&_p:last-child]:mb-0
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ul]:space-y-1
              [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_ol]:space-y-1
              [&_li]:text-gray-600
              [&_strong]:text-gray-800 [&_strong]:font-semibold"
            dangerouslySetInnerHTML={{ __html: item.answer }}
          />
          {item.linkText && item.linkHref && (
            <a
              href={item.linkHref}
              className="inline-flex items-center mt-4 text-brand-primary font-medium hover:underline transition-colors"
            >
              <span className="mr-1">&rarr;</span>
              {item.linkText}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CATEGORY SECTION COMPONENT
// ============================================================

interface CategorySectionProps {
  category: FAQCategory;
  filteredItems: FAQItem[];
  openItems: Set<string>;
  onToggle: (id: string) => void;
}

function CategorySection({ category, filteredItems, openItems, onToggle }: CategorySectionProps) {
  if (filteredItems.length === 0) return null;

  return (
    <section id={category.id} className="scroll-mt-24">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-brand-primary mb-2">
          {category.title}
        </h2>
        <p className="text-gray-500 italic">
          {category.intro}
        </p>
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-brand-border p-4 md:p-6">
        {filteredItems.map((item) => (
          <AccordionItem
            key={item.id}
            item={item}
            isOpen={openItems.has(item.id)}
            onToggle={() => onToggle(item.id)}
          />
        ))}
      </div>
    </section>
  );
}

// ============================================================
// SIDEBAR COMPONENT (Desktop)
// ============================================================

interface SidebarProps {
  categories: FAQCategory[];
  activeCategory: string | null;
  filteredCounts: Record<string, number>;
}

function Sidebar({ categories, activeCategory, filteredCounts }: SidebarProps) {
  return (
    <nav className="sticky top-24 space-y-1">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 px-3">
        Categories
      </h4>
      {categories.map((category) => {
        const count = filteredCounts[category.id] || 0;
        if (count === 0) return null;

        return (
          <a
            key={category.id}
            href={`#${category.id}`}
            className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
              activeCategory === category.id
                ? 'bg-brand-primary/10 text-brand-primary font-medium'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <span>{category.title}</span>
            <span className="text-xs text-gray-400">{count}</span>
          </a>
        );
      })}
    </nav>
  );
}

// ============================================================
// SEARCH BAR COMPONENT
// ============================================================

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  totalResults: number;
  totalQuestions: number;
}

function SearchBar({ value, onChange, totalResults, totalQuestions }: SearchBarProps) {
  return (
    <div className="relative max-w-2xl mx-auto">
      <div className="relative">
        <Search
          size={20}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search questions... e.g. 'title deed', 'stamp duty', 'diaspora'"
          className="w-full pl-12 pr-12 py-4 rounded-2xl border border-brand-border bg-white shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-shadow"
          aria-label="Search FAQ questions"
        />
        {value && (
          <button
            onClick={() => onChange('')}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            aria-label="Clear search"
          >
            <X size={18} />
          </button>
        )}
      </div>
      {value && (
        <p className="mt-2 text-sm text-gray-500 text-center">
          {totalResults === 0
            ? 'No questions match your search. Try different keywords.'
            : `Showing ${totalResults} of ${totalQuestions} questions`}
        </p>
      )}
    </div>
  );
}

// ============================================================
// MAIN FAQ PAGE COMPONENT
// ============================================================

export default function FAQPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Filter items based on search query
  const { filteredCategories, filteredCounts, totalResults } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const counts: Record<string, number> = {};
    let total = 0;

    const filtered = FAQ_CATEGORIES.map((category) => {
      const matchingItems = category.items.filter((item) => {
        if (!query) return true;
        const questionMatch = item.question.toLowerCase().includes(query);
        const answerMatch = item.answer.toLowerCase().includes(query);
        return questionMatch || answerMatch;
      });
      counts[category.id] = matchingItems.length;
      total += matchingItems.length;
      return { ...category, items: matchingItems };
    });

    return { filteredCategories: filtered, filteredCounts: counts, totalResults: total };
  }, [searchQuery]);

  const totalQuestions = FAQ_CATEGORIES.reduce((sum, cat) => sum + cat.items.length, 0);

  // Toggle accordion item
  const handleToggle = useCallback((id: string) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Track active category on scroll
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      const sections = FAQ_CATEGORIES.map((cat) => ({
        id: cat.id,
        element: document.getElementById(cat.id),
      })).filter((s) => s.element);

      for (const section of sections) {
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom > 150) {
            setActiveCategory(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-brand-bg">
      <HeaderSSR />

      {/* Hero Section */}
      <header className="pt-24 pb-12 px-4 bg-gradient-to-b from-white to-brand-bg">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-primary/10 mb-6">
            <HelpCircle className="w-8 h-8 text-brand-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {FAQ_META.h1}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            {FAQ_META.subtitle}
          </p>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            totalResults={totalResults}
            totalQuestions={totalQuestions}
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex gap-12">
          {/* Sidebar (Desktop) */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <Sidebar
              categories={FAQ_CATEGORIES}
              activeCategory={activeCategory}
              filteredCounts={filteredCounts}
            />
          </aside>

          {/* FAQ Content */}
          <div className="flex-1 space-y-12 min-w-0">
            {totalResults === 0 && searchQuery ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  No questions found
                </h2>
                <p className="text-gray-500 max-w-md mx-auto">
                  No questions match "{searchQuery}". Try different keywords or{' '}
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-brand-primary hover:underline"
                  >
                    clear your search
                  </button>{' '}
                  to see all questions.
                </p>
              </div>
            ) : (
              filteredCategories.map((category) => (
                <CategorySection
                  key={category.id}
                  category={category}
                  filteredItems={category.items}
                  openItems={openItems}
                  onToggle={handleToggle}
                />
              ))
            )}

            {/* Legal Disclaimer */}
            <div className="mt-12 p-6 bg-gray-50 rounded-2xl border border-brand-border">
              <p className="text-sm text-gray-500 leading-relaxed">
                <strong className="text-gray-600">Disclaimer:</strong> The information on this page is provided as general guidance only and does not constitute legal, financial, or professional advice. Property laws, tax rates, and government processes in Kenya may change. Always consult a qualified conveyancing lawyer or financial advisor before making property decisions. Afriqahome is a marketplace platform — we do not provide legal services or guarantee the accuracy of third-party information.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* CTA Section */}
      <section className="bg-white border-t border-brand-border py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Still have questions?
          </h2>
          <p className="text-gray-600 mb-8">
            Can't find what you're looking for? Browse our verified property listings or read our blog for more insights on Kenya real estate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/for-sale"
              className="inline-flex items-center justify-center px-8 py-3 bg-brand-primary text-white font-semibold rounded-full hover:bg-brand-primary/90 transition-colors shadow-sm"
            >
              Browse Properties
            </a>
            <a
              href="/guides"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-brand-primary font-semibold rounded-full border-2 border-brand-primary hover:bg-brand-primary/5 transition-colors"
            >
              Read Our Guides
            </a>
          </div>
        </div>
      </section>

      <FooterSSR />
    </div>
  );
}
